import { useEffect, useRef } from "react";

import { trackEvent } from "~/lib/analytics";
import { isInStandaloneMode } from "~/lib/ios-install";
import { logger } from "~/lib/logger";
import type { PwaDeviceSurface, PwaInstallSurface } from "~/lib/pwa";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useUserAgent } from "~/context/UserAgentContext";

// AIDEV-NOTE: Renders nothing. When an authenticated user opens the app in installed
// PWA/standalone mode, this records the launch surface (mobile|desktop) server-side
// (idempotent, merges to "both") and fires a one-time "pwa launched" analytics event the first
// time each surface is seen. Lives separately from InstallAppPrompt (which only shows when NOT
// standalone) precisely because it must run in the opposite case — when the app IS standalone.

interface RecordPwaLaunchResponse {
    changed: boolean;
    installSurface: PwaInstallSurface;
}

export function PwaLaunchTracker() {
    const { userId } = useSupabaseSession();
    const { isMobile, isTablet } = useUserAgent();
    // Guard against double-firing (React StrictMode / re-renders) within a single page load.
    const recordedRef = useRef(false);

    useEffect(() => {
        if (!userId) return;
        if (!isInStandaloneMode()) return;
        if (recordedRef.current) return;
        recordedRef.current = true;

        const surface: PwaDeviceSurface =
            isMobile || isTablet ? "mobile" : "desktop";

        void fetch("/api/pwa-launched", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ surface }),
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((result: RecordPwaLaunchResponse | null) => {
                // Only fire the event the first time we see this surface for the user.
                if (result?.changed) {
                    trackEvent("pwa launched", {
                        surface,
                        installSurface: result.installSurface,
                    });
                }
            })
            .catch((error: unknown) => {
                const normalizedError =
                    error instanceof Error ? error : new Error(String(error));
                logger
                    .withError(normalizedError)
                    .debug("Failed to record PWA launch");
            });
    }, [userId, isMobile, isTablet]);

    return null;
}
